import React from 'react';
import * as R from 'ramda';
import {CountryCode} from 'libphonenumber-js';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PhoneIcon from '@mui/icons-material/Phone';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormHelperText from '@mui/material/FormHelperText';
import {Input} from '@practicegenius/pg-ui/src/base/Material/components/Input';
import {Checkbox} from '@practicegenius/pg-ui/src/base/Material/components/Checkbox';
import {Button} from '@practicegenius/pg-ui/src/base/Material/components/Button';
import {PromotionType} from '@practicegenius/domain/src/data/promotions/promotion';
import {PhoneNumberInput} from '@practicegenius/pg-ui/src/base/Material/components/PhoneNumberField';
import {makeSxStyles} from '@practicegenius/pg-ui/src/base/Material/utils/sx-helpers';
import {setType} from '@practicegenius/pg-util/src/TypescriptUtils';
import {usePhpJs} from '../../../utils/php-js';
import {IsMember} from '../types';
import {
  LandingPageSignUpFormik,
  LandingPageSignUpFormValues,
} from './LandingPageSignUpForm';
import {InterestedParty, InterestedPartyType} from './InterestedParty';
import lang from '../lang';

type Props = {
  title: string;
  subtitle: string;
  buttonText: string;
  formik: LandingPageSignUpFormik;
};

const inputWidth = 41;

const useStyles = makeSxStyles(theme => ({
  subtitle: {
    display: 'none',
    textAlign: 'center',
    [theme.breakpoints.up('sm')]: {
      display: 'block',
    },
  },
  fields: {
    display: 'flex',
    flexDirection: 'column',
    m: theme.spacing(2),
    width: '100%',
  },
  inputRow: {
    display: 'flex',
    flexDirection: 'column',
    [theme.breakpoints.up('sm')]: {
      flexDirection: 'row',
      justifyContent: 'center',
      '&>:first-child': {
        mr: theme.spacing(2),
      },
    },
    [theme.breakpoints.up('md')]: {
      '&>:first-child': {
        mr: theme.spacing(9),
      },
    },
  },
  input: {
    mt: 0,
    mb: theme.spacing(2),
    [theme.breakpoints.up('sm')]: {
      width: theme.spacing(inputWidth),
    },
  },
  postFields: {
    alignSelf: 'stretch',
    [theme.breakpoints.up('sm')]: {
      alignSelf: 'center',
    },
  },
  button: {
    width: '100%',
    borderRadius: 50,
    textTransform: 'capitalize',
    padding: theme.spacing(1, 5),
    boxShadow: 'none',
    [theme.breakpoints.up('sm')]: {
      width: theme.spacing(inputWidth - 5),
    },
  },
}));

export const ContactInfoForm: React.FC<Props> = ({
  title,
  subtitle,
  buttonText,
  formik,
}) => {
  const [styles] = useStyles();

  const country = usePhpJs(
    R.pipe(R.path(['practice', 'country']), setType<CountryCode>())
  );

  const makeFieldProps = (
    field: keyof LandingPageSignUpFormValues,
    label?: string
  ) => {
    return {
      ...formik.getFieldProps(field),
      label: label ?? R.propOr('', field, lang.contactInfoFields),
      sx: styles.input,
    };
  };

  return (
    <Box display="flex" flexDirection="column" alignItems="center">
      <Typography variant="h6">{title}</Typography>
      <Typography variant="subtitle2" gutterBottom sx={styles.subtitle}>
        {subtitle}
      </Typography>
      <Box
        sx={styles.fields}
        style={formik.isSubmitting ? {filter: 'blur(3px)'} : {}}
      >
        <Box sx={styles.inputRow}>
          <Input required {...makeFieldProps('firstName')} />
          <Input required {...makeFieldProps('lastName')} />
        </Box>
        <Box sx={styles.inputRow}>
          <PhoneNumberInput
            icon={PhoneIcon}
            required
            countryCode={country}
            {...makeFieldProps('phone')}
          />
          <Input icon={MailOutlineIcon} required {...makeFieldProps('email')} />
        </Box>
        <Box display="flex" flexDirection="column" sx={styles.postFields}>
          {formik.values.promotionType === PromotionType.offer ? (
            <>
              <FormControl
                sx={styles.input}
                error={
                  formik.touched.isMember && Boolean(formik.errors.isMember)
                }
              >
                <Select
                  variant="outlined"
                  margin="dense"
                  {...{
                    ...R.omit(
                      [
                        'error',
                        'label',
                        // @ts-expect-error
                        'className',
                      ],
                      makeFieldProps('isMember')
                    ),
                  }}
                >
                  <MenuItem value="null" disabled style={{display: 'none'}}>
                    {lang.signUp.postInfoFields.isMember.label}
                  </MenuItem>
                  {Object.entries(
                    lang.signUp.postInfoFields.isMember.options
                  ).map(([key, val]) => (
                    <MenuItem key={key} value={key} disableRipple>
                      {val}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  {formik.touched.isMember &&
                    formik.errors.isMember &&
                    lang.signUp.postInfoFields.isMember.requiredError}
                </FormHelperText>
              </FormControl>
              {formik.values.isMember !== IsMember.NULL && (
                <InterestedParty
                  interestedParty={formik.values.interestedParty}
                  langStrings={lang.signUp.postInfoFields}
                  onChange={formik.getFieldProps('interestedParty').onChange}
                />
              )}
            </>
          ) : (
            <InterestedParty
              interestedParty={formik.values.interestedParty}
              langStrings={lang.contactMe.postInfoFields}
              onChange={formik.getFieldProps('interestedParty').onChange}
            />
          )}
        </Box>
        {formik.values.interestedParty === InterestedPartyType.myChild && (
          <Box sx={styles.inputRow} mt={2}>
            <Input required {...makeFieldProps('childFirstName')} />
            <Input required {...makeFieldProps('childLastName')} />
          </Box>
        )}
      </Box>
      <Box display="none">
        <Checkbox
          {...R.omit(
            [
              // @ts-expect-error
              'className',
              'error',
            ],
            makeFieldProps('isNotRealUser')
          )}
        />
      </Box>
      <Button
        sx={styles.button}
        color="primary"
        loading={formik.isSubmitting}
        type="submit"
        data-testid={
          formik.errors.isNotRealUser || formik.errors.honeyTimeElapsed
            ? 'ContactFormInfo__submit-asNonHuman'
            : 'ContactFormInfo__submit'
        }
      >
        <Typography>{buttonText}</Typography>
      </Button>
    </Box>
  );
};
